/**
 * Immersion — the page easing down so the header scrolls off and one element fills the screen.
 *
 * Live's /lookbooks pages do this (SAVETTE-66), and the lookbook here was built to match: 800ms
 * after it mounts the page eases down to the top of the spreads, and every flip by click or arrow
 * key eases it back there if the shopper has scrolled the page since. The Process and Care Guide
 * pages asked for the same (SAVETTE-70, the client: "just like the lookbook template pages do"),
 * so the mechanics live here and both elements compose them: lookbook.js (<lookbook-flip>) and
 * process-tabs.js (<process-tabs>). Every delay, guard and condition below is the lookbook's as it
 * was QA'd; the lookbook's behavior is unchanged by the move.
 *
 * The host element creates one with the element whose top is the immersed position, calls
 * connect() once it has it (which arms the arrival) and disconnect() in its disconnectedCallback,
 * and calls immerse() whenever the shopper uses it — never on a scroll, a swipe, a drag or a
 * wheel, which are the shopper moving the page themselves.
 *
 * Nothing moves on a phone, a tablet or below 800px, or in the theme editor (see immersive).
 * A host can narrow that further with its own condition (process-tabs.js: only while the page
 * is folded into tabs).
 *
 * How it is loaded: each host's section puts this file's versioned asset URL on the element
 * (data-immersion-src, from asset_url) and the host imports it from there in its connectedCallback
 * — loadImmersion() in lookbook.js and process-tabs.js. A static `import './immersion.js'` would
 * fetch it without its ?v= cache-busting version, so an update could reach a shopper out of step
 * with its hosts; an import map would cost every browser without one (Safari before 16.4) the
 * whole lookbook script. Dynamic import works wherever module scripts do. The sections also
 * modulepreload the same URL, so it is fetched alongside the host's script, and is nearly always
 * ready by the time the host asks for it.
 *
 * The arrival still counts from the host's connectedCallback, not from the import: the host
 * notes that moment and the page's scroll position there and hands both to connect(), which arms
 * the timer for whatever is left of IMMERSE_DELAY.
 */
const IMMERSE_MIN_WIDTH = 800;
const IMMERSE_DELAY = 800;
/** How recently the shopper must have scrolled the page for us to count them as mid-scroll. */
const USER_SCROLL_QUIET = 200;

export class Immersion {
  /**
   * @param {() => Element} target - the element whose top the page eases to, read at each use
   *   (the lookbook's track; the process section itself)
   * @param {{ when?: () => boolean }} [options] - a further condition of the host's own, read at
   *   the same moments as the rest of immersive
   */
  constructor(target, { when } = {}) {
    this.target = target;
    this.when = when;
  }

  /**
   * Listen for the shopper's own scrolling and arm the arrival.
   *
   * @param {{ at?: number, scrollY?: number }} [arrival] - when the host connected
   *   (performance.now()) and where the page was then; the arrival fires IMMERSE_DELAY after that
   *   moment and only if the page is still there. Both default to now.
   */
  connect({ at = performance.now(), scrollY = window.scrollY } = {}) {
    window.addEventListener('scroll', this.onWindowScroll, { passive: true });
    this.startY = scrollY;
    const wait = Math.max(0, IMMERSE_DELAY - (performance.now() - at));
    if (this.immersive) this.immerseTimer = setTimeout(this.immerseOnArrival, wait);
  }

  disconnect() {
    window.removeEventListener('scroll', this.onWindowScroll);
    clearTimeout(this.immerseTimer);
    clearTimeout(this.aligningTimer);
    if (this.alignDone) window.removeEventListener('scrollend', this.alignDone);
    this.alignDone = null;
    this.aligning = false;
  }

  /**
   * Live brings the lookbook into full view only at 800px and wider on a device that is not a
   * phone or tablet. A fine primary pointer that can hover is the same line drawn without
   * sniffing the user agent. Not in the theme editor, where the page jumping under the merchant
   * would get in the way of editing it. Read at the moment of each action, so a window resized
   * below 800 stops moving.
   */
  get immersive() {
    if (window.Shopify?.designMode) return false;
    return (
      window.innerWidth >= IMMERSE_MIN_WIDTH &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
      (this.when?.() ?? true)
    );
  }

  /**
   * Where the page sits when the content fills the screen: the target's top, measured each time
   * rather than fixed, because the header's height (and the banner above it) is the merchant's.
   */
  get immersedTop() {
    return Math.round(this.target().getBoundingClientRect().top + window.scrollY);
  }

  /** The shopper scrolled the page in the last moment, with anything but our own easing. */
  get userScrolling() {
    return performance.now() - (this.userScrolledAt ?? -Infinity) < USER_SCROLL_QUIET;
  }

  onWindowScroll = () => {
    if (!this.aligning) this.userScrolledAt = performance.now();
  };

  /**
   * The arrival: only if the shopper has not already scrolled the page, has not moved focus
   * (a keyboard user tabbing through the header keeps their place), and has not asked for
   * reduced motion — an unasked-for jump of the whole page is exactly the motion they turned off.
   */
  immerseOnArrival = () => {
    this.immerseTimer = null;
    if (Math.abs(window.scrollY - this.startY) > 1) return;
    if (document.activeElement && document.activeElement !== document.body) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.immerse();
  };

  /**
   * Ease the page to the target's top, unless it is there already, the screen is too narrow or
   * touch-driven, or the shopper is scrolling right now — a page that snatches the scroll from
   * under a moving wheel is worse than a header left showing.
   *
   * Live re-aligns the lookbook on forward flips only. Kylie asked for it whenever the lookbook
   * is used (QA, 9 Oct), so a backward click or ArrowLeft brings the spreads back into view too;
   * only the shopper's own swipe, drag or wheel is left to move the page alone. When the hosts
   * call this is theirs to say; this only decides whether the page moves, and how.
   *
   * Reduced motion: the arrival never runs, and a move the shopper asked for by using the
   * content jumps instead of easing.
   */
  immerse() {
    if (!this.immersive || this.userScrolling) return;
    const top = this.immersedTop;
    if (Math.abs(window.scrollY - top) <= 1) return;

    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // our own scroll events are not the shopper's; stop ignoring them once the easing ends
    this.aligning = true;
    clearTimeout(this.aligningTimer);
    if (this.alignDone) window.removeEventListener('scrollend', this.alignDone);
    const done = () => {
      clearTimeout(this.aligningTimer);
      window.removeEventListener('scrollend', done);
      if (this.alignDone === done) this.alignDone = null;
      this.aligning = false;
    };
    this.alignDone = done;
    window.addEventListener('scrollend', done, { once: true });
    this.aligningTimer = setTimeout(done, still ? 100 : 1000);
    window.scrollTo({ top, behavior: still ? 'auto' : 'smooth' });
  }
}
